import { createContext, useContext } from "react";
import type { PasswordResetError, PasswordUpdateError } from "./password-policy";

export type EmployeeAccessContext = {
  employeeId: string;
  workEmail: string;
  fullNameAr: string | null;
  fullNameEn: string;
  status: "invited" | "active" | "disabled";
  locale: "ar" | "en";
  displayTimezone: "Asia/Riyadh";
  primaryTeamId: string | null;
  roles: string[];
  permissions: string[];
};

export type AuthState =
  | { kind: "loading" }
  | { kind: "signed_out" }
  | { kind: "recovery" }
  | { kind: "recovery_invalid" }
  | { kind: "invited"; access: EmployeeAccessContext }
  | { kind: "disabled"; access: EmployeeAccessContext }
  | { kind: "active"; access: EmployeeAccessContext }
  | { kind: "error"; message: string };

export type AuthContextValue = {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<string | null>;
  requestPasswordReset: (email: string) => Promise<PasswordResetError | null>;
  verifyRecoveryCode: (email: string, token: string) => Promise<"recovery_code_invalid" | null>;
  updatePassword: (password: string) => Promise<PasswordUpdateError | null>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthContext is unavailable.");
  return value;
}
